import React from 'react';
import { CheckIcon, CircleAlertIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { AlertItem, Severity } from '../data/operations';
type AlertFeedProps = {
  alerts: AlertItem[];
  onAcknowledge: (id: string) => void;
};
const styles: Record<Severity, {
  border: string;
  text: string;
  Icon: typeof CircleAlertIcon;
  label: string;
}> = {
  critical: {
    border: 'border-l-red-400',
    text: 'text-red-300',
    Icon: CircleAlertIcon,
    label: 'Critical'
  },
  warning: {
    border: 'border-l-amber-400',
    text: 'text-amber-300',
    Icon: TriangleAlertIcon,
    label: 'Warning'
  },
  info: {
    border: 'border-l-cyan-400',
    text: 'text-cyan-300',
    Icon: InfoIcon,
    label: 'Information'
  }
};
export function AlertFeed({
  alerts,
  onAcknowledge
}: AlertFeedProps) {
  const reduceMotion = useReducedMotion();
  return <section className="flex min-h-[390px] flex-col border border-slate-700/80 bg-[#09131c]" aria-labelledby="alerts-heading" data-id="element-1">
      <div className="flex items-center justify-between border-b border-slate-700/70 px-3 py-2.5" data-id="element-2">
        <div data-id="element-3">
          <h2 id="alerts-heading" className="font-display text-xs font-bold tracking-[0.16em] text-slate-100" data-id="element-4">
            ACTIVE ALERTS
          </h2>
          <p className="mt-0.5 font-mono text-[9px] text-slate-500" data-id="element-5">
            RANKED BY OPERATIONAL IMPACT
          </p>
        </div>
        <span className="border border-red-400/30 bg-red-400/5 px-2 py-1 font-mono text-[9px] text-red-300" data-id="element-6">
          {alerts.length} OPEN
        </span>
      </div>

      <div className="max-h-[344px] flex-1 overflow-y-auto" data-id="element-7">
        <AnimatePresence initial={false} data-id="element-8">
          {alerts.length > 0 ? alerts.map(alert => {
          const {
            border,
            text,
            Icon,
            label
          } = styles[alert.severity];
          return <motion.article key={alert.id} layout={!reduceMotion} initial={reduceMotion ? false : {
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={reduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            height: 0,
            x: 20
          }} className={`group border-b border-l-2 border-b-slate-800 px-3 py-3 ${border}`} data-id="element-9">
                  <div className="flex gap-2.5" data-id="element-10">
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${text}`} aria-hidden="true" data-id="element-11" />
                    <div className="min-w-0 flex-1" data-id="element-12">
                      <div className="flex items-start justify-between gap-2" data-id="element-13">
                        <p className="text-xs font-semibold leading-4 text-slate-100" data-id="element-14">
                          {alert.title}
                        </p>
                        <time className="shrink-0 font-mono text-[9px] text-slate-500" data-id="element-15">
                          {alert.time}
                        </time>
                      </div>
                      <p className="mt-1 text-[10px] leading-4 text-slate-400" data-id="element-16">
                        {alert.detail}
                      </p>
                      <div className="mt-2 flex items-center justify-between" data-id="element-17">
                        <span className={`font-mono text-[8px] tracking-wider ${text}`} data-id="element-18">
                          {label.toUpperCase()} / {alert.code}
                        </span>
                        <button onClick={() => onAcknowledge(alert.id)} className="inline-flex items-center gap-1 font-mono text-[9px] text-slate-500 opacity-100 transition-colors hover:text-lime-300 focus:outline-none focus:ring-1 focus:ring-cyan-400 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100" aria-label={`Acknowledge ${alert.title}`} data-id="element-19">
                          <CheckIcon className="h-3 w-3" data-id="element-20" /> ACK
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.article>;
        }) : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="grid min-h-64 place-items-center p-8 text-center" data-id="element-21">
              <div data-id="element-22">
                <CheckIcon className="mx-auto h-6 w-6 text-lime-400" data-id="element-23" />
                <p className="mt-3 text-sm font-semibold text-slate-200" data-id="element-24">
                  Alert queue cleared
                </p>
                <p className="mt-1 text-xs text-slate-500" data-id="element-25">
                  No active operational exceptions.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}